import React, { useState } from 'react';
import dayjs from 'dayjs';
import AnalyticsCard from '../../../components/Analytics/AnalyticsCard/AnalyticsCard';
import GenericLineChart from '../GenericLineChart/GenericLineChart';
import ConversationsResponse from '../../../types/Analytics/ConversationsResponse';
import generateColours from '../generateColours';

interface ConversationsChartProps {
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  conversationsData: ConversationsResponse[] | undefined;
  isLoading: boolean;
  isSmallScreen: boolean;
}

function ConversationsChart({
  dateRange,
  conversationsData,
  isLoading,
  isSmallScreen
}: ConversationsChartProps): React.ReactElement {
  const [selectedGroups, setSelectedGroups] = useState<string[]>([
    'totalMessages',
    'totalConversations'
  ]);

  const colours = generateColours(2);
  const legendItems = [
    { label: 'Total messages', key: 'totalMessages', color: colours[0] },
    {
      label: 'Total conversations',
      key: 'totalConversations',
      color: colours[1]
    }
  ];

  const handleToggleGroup = (key: string): void => {
    setSelectedGroups((prev) => {
      if (prev.includes(key)) {
        return prev.filter((item) => item !== key);
      }
      return [...prev, key];
    });
  };

  return (
    <AnalyticsCard
      isLoading={isLoading}
      title='Conversations'
      tooltipText='Total messages and conversations during the selected period.'
      className='mt-4 pb-4'
      style={{ height: 480 }}
      content={
        <div className='flex flex-col lg:flex-row w-full'>
          <div className='flex-1 min-w-0'>
            <GenericLineChart
              dateRange={dateRange}
              data={conversationsData}
              dateKey='date'
              groupKeys={['totalMessages', 'totalConversations']}
              groupKeyLabels={{
                totalMessages: 'Total messages',
                totalConversations: 'Total conversations'
              }}
              isSmallScreen={isSmallScreen}
              selectedGroups={selectedGroups}
              height={380}
              hideLegend
            />
          </div>
          <div className='flex flex-wrap gap-3 justify-center lg:flex-col lg:gap-3 lg:pt-8 lg:pr-12 lg:justify-start'>
            {legendItems.map((item) => {
              const isSelected = selectedGroups.includes(item.key);
              return (
                <button
                  key={item.key}
                  onClick={() => handleToggleGroup(item.key)}
                  type='button'
                  className='flex items-center gap-2 px-2 py-1 rounded transition-colors hover:bg-gray-100 whitespace-nowrap'
                  style={{ opacity: isSelected ? 1 : 0.5 }}
                >
                  <div
                    className='h-3 w-3 rounded-full flex-shrink-0'
                    style={{ backgroundColor: item.color }}
                  />
                  <span className='text-sm text-gray-700'>{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      }
    />
  );
}

export default ConversationsChart;
